import React, { ReactElement } from 'react';
import { Link, useParams } from 'react-router-dom';
import classNames from 'classnames';
import { ApolloQueryResult } from '@apollo/client';
import { Spacing } from '@atlaskit/button';
import EditIcon from '@atlaskit/icon/glyph/edit';
import { Flex, Text } from '@atlaskit/primitives';
import Lozenge from '@atlaskit/lozenge';
import { PaymentMethodId, PaymentMethodPanel } from '@atlassian/commerce-payment-methods';
import { PaymentMethodRequest } from '@atlassian/cssxp-types-billing';
import { sentenceCase } from '@atlassian/cssxp-utils-generic';
import {
  AchPanel,
  ContactsTooltip,
  ContactsTooltipType,
  CopyableText,
  ErrorComponents,
  FormattedAddress,
  OpenSidebarButton,
  PaymentErrorLozenge,
  ShowMoreLess,
  SummaryPanelContainer,
  SummaryPanelIcon,
  SummaryPanelRow,
  useFeatureToggle,
} from '@css-xp/base';
import { ManagePaymentMethodsSidebarProps } from '@css-xp/base/common-sidebars';
import {
  CreditCard,
  FetchInvoiceGroupPpcQuery,
  Maybe,
  PaymentMethod,
  PaymentMethodType,
  PaymentMethodTypeV2,
  PaymentOnTerms,
  TransactionAccount,
} from '@css-xp/graphtypes/src/generated-types-new';
import { DEFAULT_INFLIGHT_BANNER_MESSAGE, failureMessage, NoData, SoldToAddressDetailsLink } from '@css-xp/ppc-common';
import { getTaxIdLabels } from '@css-xp/ppc-tax-component';
import { useConsentHub } from '@css-xp/ppc-consent-hub';
import { COMMERCIAL_PRICE_ELIGIBILITY, STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { TermPaymentMethod } from '@css-xp/base/term-payment-method';
import { ADVOCATE_ACCESS_TYPE, ADVOCATE_RESOURCE_TYPE, AdvocateAccessWall } from '@css-xp/ppc-access-control';
import { ContactType, EndCustomerContacts } from '@css-xp/contact-resources';
import { LayoutAppearance } from '@css-xp/ppc-types';
import { PaypalPaymentMethod } from '@css-xp/ppc-paypal';
import { EditShipToPartyContentEnum } from '@css-xp/ppc-ship-to-party-sidebar';
import { SoldToAddressDisabledTooltip } from '@css-xp/ppc-customer-address-page';
import { isCommercialPricingEligibility, isAchPaymentMethod } from '@css-xp/ppc-utils';
import { SoldToAddressDetailsWrapper } from './sold-to-address-details-wrapper';
import './billing-profile-summary.scss';

interface Props {
  invoiceGroup: FetchInvoiceGroupPpcQuery;
  managePaymentMethodsSidebar: string;
  manageBillingProfileNameSidebar: string;
  submitAction: (_request: PaymentMethodRequest) => Promise<PaymentMethod>;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  layoutAppearance?: LayoutAppearance;
  invoiceGroupName?: string;
  manageBillingProfileAddressSidebar: string;
  isPaypalSupportEnabled?: boolean;
  isBillingProfileUsedForInternalUsageLicense: boolean;
  isFetchingIULDetails: boolean;
  isSoldToAddressArchivalEnabled?: boolean;
  shouldShowACHPaymentMethod?: boolean;
}

interface PreparePayloadForSidebarArgs {
  invoiceGroupId: string;
  transactionAccount: TransactionAccount;
  name?: string;
  defaultPaymentMethod: Maybe<PaymentMethod>;
  updatePaymentMethodLink?: string;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  // eslint-disable-next-line no-unused-vars
  submitAction: (request: PaymentMethodRequest) => Promise<PaymentMethod>;
  isTermPaymentMethodAllowed?: boolean;
  showAdditionalPaymentMethods?: boolean;
  successStateProps?: {
    header: string;
    description: string;
  };
  errorStateProps?: {
    header: string;
    description: ReactElement | string;
  };
  showDisclaimer?: boolean;
  heading?: string;
  inflightBannerMessage?: string;
  linkedEntitlementHeading?: string;
  isOneColumnLayout?: boolean;
  isPaypalSupportEnabled?: boolean;
  shouldShowACHPaymentMethod?: boolean;
}

interface RenderPaymentMethodBtnOptions {
  isDisabled: boolean;
  sidebarHeading: string;
  btnText: string;
  testId: string;
  buttonSpacing?: Spacing;
}

export const DISCLAIMER_TEXT_PARAGRAPH_1 = `If you confirm, any changes made to your payment method will automatically apply to all subscriptions with the same sold-to address. All subscriptions that use the current payment method will also get updated. We’ll charge the updated payment method for these subscriptions.`;
export const DISCLAIMER_TEXT_PARAGRAPH_2 = `You won’t be charged immediately. In most cases, you'll be charged for the subscription on the first day of each billing period using the payment method you’ve specified above. Atlassian will keep your payment info securely on file.`;
export const DISCLAIMER_TEXT_PARAGRAPH_3 = `Your billing statement will display Atlassian B.V., located in Amsterdam, Netherlands. Atlassian Pty Limited, our principal place of business, is at Level 6, 341 George Street, Sydney NSW Australia 2000.`;
export const DISCLAIMER_TEXT_PARAGRAPH_4 = `Your credit card issuer may charge foreign transaction or cross-border fees in addition to the total price.`;

const CHANGE_PAYMENT_METHOD_CONSENT_TEXT = `${DISCLAIMER_TEXT_PARAGRAPH_1} ${DISCLAIMER_TEXT_PARAGRAPH_2} ${DISCLAIMER_TEXT_PARAGRAPH_3} ${DISCLAIMER_TEXT_PARAGRAPH_4}`;

const preparePayloadForSidebar = ({
  invoiceGroupId,
  transactionAccount,
  defaultPaymentMethod,
  name,
  refetch,
  submitAction,
  isTermPaymentMethodAllowed,
  showAdditionalPaymentMethods,
  errorStateProps,
  successStateProps,
  showDisclaimer,
  heading,
  inflightBannerMessage,
  linkedEntitlementHeading,
  isOneColumnLayout,
  isPaypalSupportEnabled,
  shouldShowACHPaymentMethod,
}: PreparePayloadForSidebarArgs): ManagePaymentMethodsSidebarProps => {
  return {
    invoiceGroupId,
    transactionAccountId: transactionAccount?.id as string,
    name: name,
    allPaymentMethods: transactionAccount?.availablePaymentMethods?.nodes,
    defaultPaymentMethodId: defaultPaymentMethod?.id as string,
    defaultPaymentMethodType: defaultPaymentMethod?.type as string,
    refetch,
    submitAction,
    isTermPaymentMethodAllowed,
    showAdditionalPaymentMethods,
    errorStateProps,
    successStateProps,
    showDisclaimer,
    disclaimerContent: isOneColumnLayout
      ? undefined
      : [
          DISCLAIMER_TEXT_PARAGRAPH_1,
          DISCLAIMER_TEXT_PARAGRAPH_2,
          DISCLAIMER_TEXT_PARAGRAPH_3,
          DISCLAIMER_TEXT_PARAGRAPH_4,
        ],
    heading,
    saveButtonLabel: 'Confirm',
    inflightBannerMessage: inflightBannerMessage,
    linkedEntitlementHeading: linkedEntitlementHeading,
    isOneColumnLayout,
    isPaypalSupportEnabled,
    shouldShowACHPaymentMethod,
  };
};

export const BillingProfileSummary = ({
  invoiceGroup,
  managePaymentMethodsSidebar,
  manageBillingProfileNameSidebar,
  refetch,
  submitAction,
  layoutAppearance = LayoutAppearance.DEFAULT,
  invoiceGroupName,
  manageBillingProfileAddressSidebar,
  isPaypalSupportEnabled = false,
  isBillingProfileUsedForInternalUsageLicense,
  isFetchingIULDetails,
  isSoldToAddressArchivalEnabled = false,
  shouldShowACHPaymentMethod = false,
}: Props) => {
  const [isEnabled] = useFeatureToggle();
  const { handleWithCustomerConsent } = useConsentHub();
  const { txaId } = useParams<{ txaId: string }>();
  const isBillingProfileNotificationEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_NOTIFICATION_AT_BILLING_PROFILE);
  const isCustomerContactV3Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_CUSTOMER_CONTACT_V3);
  const isIULPhase2Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_IUL_PHASE_2);
  const shouldRenderForInternalUse = isBillingProfileUsedForInternalUsageLicense && isIULPhase2Enabled;

  if (!invoiceGroup?.invoiceGroup) {
    return (
      <ErrorComponents.ErrorPanel
        error={new ErrorComponents.NotFoundError(`Unable to find billing profiles for page summary.`)}
      />
    );
  }

  const {
    shipToParty: shipToAddress,
    defaultPaymentMethod,
    currency,
    transactionAccount,
    id,
    termPaymentStatus,
    isActive,
    invoiceGroupNumber,
  } = invoiceGroup?.invoiceGroup;
  const defaultPaymentMethodObj = defaultPaymentMethod as CreditCard;
  // convert expMonth & expYear to Numbers
  const expMonth = +(defaultPaymentMethodObj?.card?.expirationMonth ?? 0);
  const expYear = +(defaultPaymentMethodObj?.card?.expirationYear ?? 0);
  const isPaymentIssue = defaultPaymentMethodObj?.paymentIssue === true;
  const isNotDeferredPaymentMethod = defaultPaymentMethod?.type !== PaymentMethodType?.DEFERRED;
  const isOneColumnLayout = layoutAppearance === LayoutAppearance.ONE_COLUMN;
  const isChangePaymentMethodDisabled = isActive === false;
  const taxIdLabels = getTaxIdLabels(shipToAddress);
  const name = invoiceGroup?.invoiceGroup?.name;
  const billingProfileNumber = invoiceGroupNumber || id;
  const successStateProps = {
    header: 'Update successful',
    description: 'Payment method updated.',
  };
  const errorStateProps = {
    header: 'Update unsuccessful',
    description: failureMessage(),
  };
  const pricingEligibility = sentenceCase(shipToAddress?.shipToPriceEligibility ?? COMMERCIAL_PRICE_ELIGIBILITY);
  const isSoldToAddressDeactivated = isSoldToAddressArchivalEnabled && !shipToAddress?.isActive;
  const isDefaultPaymentMethodACH = shouldShowACHPaymentMethod && isAchPaymentMethod(defaultPaymentMethod?.type);

  const getCompanyName = () => {
    return (
      <SoldToAddressDetailsLink txaId={transactionAccount?.id} companyName={shipToAddress?.name ?? ''}>
        {shipToAddress?.name ?? <NoData />}
      </SoldToAddressDetailsLink>
    );
  };

  const getInvoiceGroupName = () => {
    return (
      <Flex alignItems="center" gap="space.300">
        <div>{invoiceGroupName}</div>
        <AdvocateAccessWall accessType={ADVOCATE_ACCESS_TYPE.UPDATE} resource={ADVOCATE_RESOURCE_TYPE.INVOICE_GROUP}>
          <OpenSidebarButton
            sidebar={manageBillingProfileNameSidebar}
            payload={{
              ...invoiceGroup.invoiceGroup,
              refetch,
            }}
            appearance="subtle"
            testId="edit-billing-profile-name"
            label="Edit"
            spacing="compact"
            iconBefore={<EditIcon label="Edit" size="medium" />}
            isDisabled={!isActive}
          />
        </AdvocateAccessWall>
      </Flex>
    );
  };

  const getSoldToAddress = () => {
    const shipToId = shipToAddress?.id;
    return (
      <SoldToAddressDetailsWrapper
        shipToAddress={shipToAddress}
        isBillingProfileUsedForInternalUsageLicense={isBillingProfileUsedForInternalUsageLicense}
      >
        <SoldToAddressDetailsLink
          txaId={transactionAccount?.id}
          shipToId={shipToId ? shipToId : ''}
          shouldDisableLink={isOneColumnLayout}
        >
          <div className={`address-details ${isSoldToAddressDeactivated ? 'subtlest' : ''}`}>
            <FormattedAddress
              addressLine1={shipToAddress?.addressLine1}
              addressLine2={shipToAddress?.addressLine2}
              city={shipToAddress?.city}
              country={shipToAddress?.country}
              postcode={shipToAddress?.postcode}
              state={shipToAddress?.state}
              hasLineBreak={!isOneColumnLayout}
            />
          </div>
        </SoldToAddressDetailsLink>
      </SoldToAddressDetailsWrapper>
    );
  };

  const createBillingProfilePayload = {
    transactionAccountId: transactionAccount?.id,
    name: name,
    billToAddress: shipToAddress,
    availablePaymentMethods: transactionAccount?.availablePaymentMethods?.nodes,
    updatePaymentMethodLink: '',
    defaultSoldToAddress: shipToAddress,
    soldToAddresses: shipToAddress,
    isDefaultAddressSelected: true,
    termPaymentStatus: termPaymentStatus?.status,
    isProgressIndicatorEnabled: isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES),
  };

  const renderPaymentMethodButton = ({
    isDisabled,
    sidebarHeading,
    btnText,
    testId,
    buttonSpacing,
  }: RenderPaymentMethodBtnOptions) => {
    return (
      <OpenSidebarButton
        appearance={isOneColumnLayout ? 'subtle' : 'link'}
        sidebar={managePaymentMethodsSidebar}
        sidebarOptions={{
          sidebarWidth: isOneColumnLayout ? undefined : '600px',
          isOverlay: !isOneColumnLayout,
          sidebarTitle: sidebarHeading,
        }}
        spacing={buttonSpacing}
        isDisabled={isDisabled}
        payload={{
          ...preparePayloadForSidebar({
            transactionAccount: transactionAccount as TransactionAccount,
            defaultPaymentMethod: defaultPaymentMethod as Maybe<PaymentMethod>,
            refetch,
            invoiceGroupId: id as string,
            name: name as string,
            submitAction: handleWithCustomerConsent(submitAction, CHANGE_PAYMENT_METHOD_CONSENT_TEXT),
            isTermPaymentMethodAllowed: true,
            showAdditionalPaymentMethods: false,
            errorStateProps: errorStateProps,
            successStateProps: successStateProps,
            showDisclaimer: true,
            heading: sidebarHeading,
            inflightBannerMessage: DEFAULT_INFLIGHT_BANNER_MESSAGE,
            linkedEntitlementHeading: 'This change will apply to these linked subscriptions:',
            isOneColumnLayout,
            isPaypalSupportEnabled,
            shouldShowACHPaymentMethod,
          }),
          createBillingProfilePayload,
        }}
        testId={testId}
        iconBefore={isOneColumnLayout ? <EditIcon label="Edit" size="medium" /> : undefined}
      >
        {btnText}
      </OpenSidebarButton>
    );
  };

  const getDefaultPaymentSection = (defaultPaymentMethod?: PaymentMethod | null) => {
    if (!defaultPaymentMethod) {
      return (
        <div className="change-payment-method">
          <p>No payment method found</p>
          <AdvocateAccessWall accessType={ADVOCATE_ACCESS_TYPE.CREATE} resource={ADVOCATE_RESOURCE_TYPE.PAYMENT_METHOD}>
            {renderPaymentMethodButton({
              isDisabled: isChangePaymentMethodDisabled,
              sidebarHeading: 'Add payment method',
              btnText: isOneColumnLayout ? '' : 'Add payment method',
              testId: 'add-payment-method-button',
              buttonSpacing: 'compact',
            })}
          </AdvocateAccessWall>
        </div>
      );
    }

    if (defaultPaymentMethod?.type === PaymentMethodType?.CARD) {
      const defaultPaymentMethodObj = defaultPaymentMethod as CreditCard;

      return (
        <div className={isOneColumnLayout ? 'payment-method-panel-container-v2' : ''}>
          <PaymentMethodPanel
            paymentMethod={{
              id: defaultPaymentMethodObj?.id as PaymentMethodId,
              type: defaultPaymentMethodObj?.type,
              // Because the 'card' prop should be used with PaymentMethodType.CARD type
              ...(defaultPaymentMethodObj?.type === PaymentMethodType.CARD && {
                card: {
                  brand: defaultPaymentMethodObj?.card?.brand ?? '',
                  expMonth,
                  expYear,
                  last4: defaultPaymentMethodObj?.card?.last4Numbers ?? '',
                  name: (!isOneColumnLayout && defaultPaymentMethodObj?.card?.cardHolderName) ?? '',
                },
              }),
              default: defaultPaymentMethodObj?.default ?? false,
            }}
          />
        </div>
      );
    }

    if (defaultPaymentMethod?.type === PaymentMethodType?.DEFERRED) {
      const defaultPaymentMethodObj = defaultPaymentMethod as PaymentOnTerms;
      const hasPayOnStatement = defaultPaymentMethodObj?.terms?.payOnStatement;
      const invoiceDueDays = defaultPaymentMethodObj?.terms?.invoiceDueDays;
      const termPaymentMethod = defaultPaymentMethodObj?.paymentMethod;
      const termPaymentType = invoiceDueDays ? `NET${invoiceDueDays}` : termPaymentMethod;
      const totalCreditLimit = defaultPaymentMethodObj?.totalCreditLimit;

      return (
        <TermPaymentMethod
          paymentNetType={hasPayOnStatement ? '' : (termPaymentType as string)}
          totalLimit={hasPayOnStatement ? undefined : (totalCreditLimit as number)}
          currentTermAccountStatus={termPaymentStatus?.status}
          hasPayOnAccount={!!hasPayOnStatement}
          version={isOneColumnLayout ? 2 : undefined}
          shouldHideVerticalDivider={Boolean(hasPayOnStatement)}
        />
      );
    }

    //@ts-ignore
    if (defaultPaymentMethod?.type === PaymentMethodTypeV2.PAYPAL) {
      const emailID = (defaultPaymentMethod as any)?.payPal?.email;
      return <PaypalPaymentMethod emailID={emailID} />;
    }

    if (isDefaultPaymentMethodACH) {
      const bankName = (defaultPaymentMethod as any)?.ach?.bankName;
      const last4 = (defaultPaymentMethod as any)?.ach?.last4;
      return <AchPanel bankName={bankName} last4={last4} showBorder showInfoInline={false} width="250px" />;
    }
  };

  const renderSoldToAddress = () => {
    if (!isOneColumnLayout) {
      return <SummaryPanelRow label="Sold-to address" value={getSoldToAddress()} />;
    }

    const EditAddressSidebarButton = () => {
      if (isSoldToAddressDeactivated) {
        return <Lozenge appearance="removed">DEACTIVATED</Lozenge>;
      }
      if (!isCommercialPricingEligibility(pricingEligibility)) {
        return <SoldToAddressDisabledTooltip />;
      }
      return (
        <AdvocateAccessWall accessType={ADVOCATE_ACCESS_TYPE.UPDATE} resource={ADVOCATE_RESOURCE_TYPE.SHIP_TO_ADDRESS}>
          <OpenSidebarButton
            sidebar={manageBillingProfileAddressSidebar}
            payload={{
              invoiceGroup: invoiceGroup?.invoiceGroup,
              transactionAccountId: transactionAccount?.id,
              refetch,
              shipToAddress,
              shouldHideHeading: true,
              shouldHideNameField: true,
              billingProfileName: name,
              billingProfileId: invoiceGroup?.invoiceGroup?.id,
              showSelectSoldToAddressDropdown: true,
              shouldHideNotificationDescription: true,
              showLinkedBillingProfiles: true,
              contentString: {
                [EditShipToPartyContentEnum.BILLING_PROFILE_UPDATE_SUCCESS]: 'Billing profile updated',
              },
            }}
            sidebarOptions={{
              sidebarTitle: 'Edit address',
              shouldAddPaddingAfterTitle: false,
            }}
            appearance="subtle"
            testId="edit-billing-profile-sold-to-address"
            label="Edit Address"
            spacing="compact"
            iconBefore={<EditIcon label="Edit" size="medium" />}
            isDisabled={!isActive}
          />
        </AdvocateAccessWall>
      );
    };

    const Component = (
      <Flex direction="column" gap="space.100">
        <Flex alignItems="center" gap="space.300">
          {getSoldToAddress()}
          {!isFetchingIULDetails && !isBillingProfileUsedForInternalUsageLicense ? <EditAddressSidebarButton /> : null}
        </Flex>
        {taxIdLabels &&
          taxIdLabels?.map((taxIdLabel) => (
            <Flex gap="space.050" key={`tax-id-label-${taxIdLabel}`}>
              <Text>{taxIdLabel.label}:</Text>
              <Text>{taxIdLabel.value}</Text>
            </Flex>
          ))}
        {/* <Flex gap="space.050">
          <Text>Tax exempt:</Text>
          <Text>{shipToAddress?.taxExempt ? 'Yes' : 'No'}</Text>
        </Flex> */}
        <Flex gap="space.050">
          <Text>Pricing eligibility: </Text>
          <div>{pricingEligibility}</div>
        </Flex>
      </Flex>
    );

    return <SummaryPanelRow label="Sold-to address" value={Component} useWrapper={isOneColumnLayout} />;
  };

  const renderInvoiceContacts = () => {
    const invoiceContacts = invoiceGroup?.invoiceGroup?.recipients?.map((recipient) => (
      <p key={recipient}>{recipient}</p>
    ));

    return (
      <SummaryPanelRow
        label="Invoice contacts"
        value={invoiceContacts?.length ? <ShowMoreLess items={invoiceContacts} itemsToShow={4} /> : <NoData />}
        testId="invoice-contacts"
        useWrapper
      />
    );
  };

  const renderPaymentMethodSection = () => {
    const Content = (
      <>
        {isPaymentIssue && isNotDeferredPaymentMethod && <PaymentErrorLozenge />}
        <div
          className={classNames('payment-method-container', {
            'payment-method-container-v2': isOneColumnLayout,
          })}
        >
          {getDefaultPaymentSection(defaultPaymentMethod)}
        </div>
        {defaultPaymentMethodObj && Object.keys(defaultPaymentMethodObj)?.length && (
          <AdvocateAccessWall accessType={ADVOCATE_ACCESS_TYPE.UPDATE} resource={ADVOCATE_RESOURCE_TYPE.PAYMENT_METHOD}>
            <div className="change-payment-method">
              {renderPaymentMethodButton({
                isDisabled:
                  defaultPaymentMethodObj?.type === PaymentMethodType.DEFERRED || isChangePaymentMethodDisabled,
                sidebarHeading: 'Change payment method',
                btnText: isOneColumnLayout ? '' : 'Change payment method',
                testId: 'manage-payment-methods-button',
              })}
            </div>
          </AdvocateAccessWall>
        )}
      </>
    );

    if (isOneColumnLayout) {
      return (
        <Flex alignItems="center" gap="space.050" testId="payment-method-section-container">
          {Content}
        </Flex>
      );
    }

    return Content;
  };

  const renderDefaultPaymentMethod = () => {
    if (defaultPaymentMethodObj && Object.keys(defaultPaymentMethodObj)?.length) {
      if (defaultPaymentMethodObj.type === PaymentMethodType.CARD) {
        return 'Credit card';
      } else if (defaultPaymentMethodObj.type === PaymentMethodType.DEFERRED) {
        return 'Payment on terms';
      } else if (defaultPaymentMethodObj.type === PaymentMethodTypeV2.PAYPAL) {
        return 'PayPal';
      } else if (isDefaultPaymentMethodACH) {
        return 'ACH debit';
      }
    }

    return 'No payment method found';
  };

  return (
    <div
      className={isOneColumnLayout ? 'billing-profile-summary-v2' : 'billing-profile-summary'}
      data-testid="billing-profile-summary"
    >
      <div className="profile-summary-section">
        <SummaryPanelContainer
          title={isOneColumnLayout ? 'Account details' : 'Billing profile overview'}
          iconKey={SummaryPanelIcon.SUBSCRIPTION_ICON}
          isHeaderContentHidden={isOneColumnLayout}
          version={isOneColumnLayout ? 2 : undefined}
        >
          {billingProfileNumber && (
            <SummaryPanelRow
              label="Billing profile number"
              value={<CopyableText>{billingProfileNumber}</CopyableText>}
              useWrapper={isOneColumnLayout}
            />
          )}
          {isOneColumnLayout && (
            <SummaryPanelRow label="Billing profile name" value={getInvoiceGroupName()} useWrapper />
          )}
          <SummaryPanelRow label="Company" value={getCompanyName()} useWrapper={isOneColumnLayout} />
          {renderSoldToAddress()}
          {!isOneColumnLayout &&
            taxIdLabels &&
            taxIdLabels?.map((taxIdLabel) => (
              <SummaryPanelRow label={taxIdLabel.label} value={taxIdLabel.value} key={`tax-id-label-${taxIdLabel}`} />
            ))}
          {!isOneColumnLayout && (
            <SummaryPanelRow label="Pricing eligibility" value={<div>{pricingEligibility}</div>} />
          )}
        </SummaryPanelContainer>
      </div>
      <div className="profile-summary-section">
        <SummaryPanelContainer
          title={isOneColumnLayout ? 'Billing information' : 'Payment method'}
          iconKey={SummaryPanelIcon.BILLING_PAYMENT_ICON}
          isHeaderContentHidden={isOneColumnLayout}
          version={isOneColumnLayout ? 2 : undefined}
        >
          <SummaryPanelRow
            label="Default payment method"
            value={renderDefaultPaymentMethod()}
            useWrapper={isOneColumnLayout}
          />
          <SummaryPanelRow
            label="Currency"
            value={currency ? currency : 'No currency found'}
            useWrapper={isOneColumnLayout}
          />
          <SummaryPanelRow label="Payment method" value={renderPaymentMethodSection()} useWrapper={isOneColumnLayout} />
        </SummaryPanelContainer>
      </div>
      {(!isCustomerContactV3Enabled || isOneColumnLayout) && invoiceGroup?.invoiceGroup?.id && (
        <div className="profile-summary-section">
          <SummaryPanelContainer
            title={isBillingProfileNotificationEnabled || isOneColumnLayout ? 'Contacts' : 'Customer contacts'}
            iconKey={SummaryPanelIcon.AVATAR_ICON}
            isHeaderContentHidden={isOneColumnLayout}
            version={isOneColumnLayout ? 2 : undefined}
          >
            <SummaryPanelRow
              label={
                shouldRenderForInternalUse ? (
                  <ContactsTooltip type={ContactsTooltipType.KEY_CONTACT} iconSize="small">
                    Key and other contacts
                  </ContactsTooltip>
                ) : (
                  <ContactsTooltip type={ContactsTooltipType.CUSTOMER} iconSize="small">
                    Customer contacts
                  </ContactsTooltip>
                )
              }
              value={
                <Flex alignItems="center" gap="space.250">
                  <EndCustomerContacts
                    invoiceGroupId={invoiceGroup?.invoiceGroup?.id}
                    transactionAccountId={txaId}
                    contactType={ContactType.CUSTOMER_CONTACT}
                    shouldRenderForInternalUse={shouldRenderForInternalUse}
                  />
                  {isOneColumnLayout && (
                    <Link to={shouldRenderForInternalUse ? '#contacts' : '#customer-contacts'}>
                      {shouldRenderForInternalUse ? 'View contacts' : 'View customer contacts'}
                    </Link>
                  )}
                </Flex>
              }
              useWrapper={isOneColumnLayout}
              testId="customer-contact-section"
            />
            {isBillingProfileNotificationEnabled && !shouldRenderForInternalUse && (
              // Should not be rendered for internal use billing profiles
              <SummaryPanelRow
                label={
                  <ContactsTooltip type={ContactsTooltipType.BILLING_CONTACT} iconSize="small">
                    Billing admins
                  </ContactsTooltip>
                }
                value={
                  <Flex alignItems="center" gap="space.150">
                    <EndCustomerContacts
                      invoiceGroupId={invoiceGroup?.invoiceGroup?.id}
                      transactionAccountId={txaId}
                      contactType={ContactType.BILLING_CONTACT}
                      shouldHidePrimaryContact
                    />
                    {isOneColumnLayout && <Link to="#billing-admins">View billing admins</Link>}
                  </Flex>
                }
                testId="billing-contact-section"
                useWrapper={isOneColumnLayout}
              />
            )}
            {isOneColumnLayout && renderInvoiceContacts()}
          </SummaryPanelContainer>
        </div>
      )}
    </div>
  );
};
